import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Event } from '../../../types';

interface CalendarViewProps {
  currentDate: Date;
  onDateChange: (date: Date) => void;
  events: Event[];
  onEventClick: (event: Event) => void;
  language: 'en' | 'ar';
  strings: any;
  getEventTypeName: (typeId: string) => string;
}

const CalendarView: React.FC<CalendarViewProps> = ({
  currentDate,
  onDateChange,
  events,
  onEventClick,
  language,
  strings,
  getEventTypeName
}) => {
  // Parse event date using the same logic as EventCard
  const parseEventDate = (dateString: string): Date | null => {
    if (!dateString) return null;
    
    try {
      // Check for DD-MM-YYYY format (European format used in your database)
      if (dateString.match(/^\d{1,2}-\d{1,2}-\d{4}$/)) {
        const parts = dateString.split('-');
        if (parts.length === 3) {
          const day = parseInt(parts[0]);   // First part is day
          const month = parseInt(parts[1]); // Second part is month
          const year = parseInt(parts[2]);  // Third part is year
          return new Date(year, month - 1, day); // Subtract 1 only when creating Date object
        }
      } else if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        // Already in YYYY-MM-DD format
        return new Date(dateString);
      } else {
        // Try direct parsing for other formats
        const parsedDate = new Date(dateString);
        return !isNaN(parsedDate.getTime()) ? parsedDate : null;
      }
    } catch (error) {
      console.warn('Error parsing date:', dateString, error);
      return null;
    }
    
    return null;
  };

  // Validate events and filter out any with invalid dates
  const validEvents = events.filter(event => {
    const eventDate = event.date || event.startDate;
    if (!eventDate) {
      return false;
    }
    
    const parsedDate = parseEventDate(eventDate);
    return parsedDate !== null;
  });
  const getLocalizedDayName = (dayIndex: number): string => {
    const dayNames = [
      strings.events.daysOfWeekShort.sunday,
      strings.events.daysOfWeekShort.monday,
      strings.events.daysOfWeekShort.tuesday,
      strings.events.daysOfWeekShort.wednesday,
      strings.events.daysOfWeekShort.thursday,
      strings.events.daysOfWeekShort.friday,
      strings.events.daysOfWeekShort.saturday,
    ];
    return dayNames[dayIndex];
  };

  const getLocalizedMonthName = (date: Date): string => {
    const monthNames = [
      strings.events.months.january,
      strings.events.months.february,
      strings.events.months.march,
      strings.events.months.april,
      strings.events.months.may,
      strings.events.months.june,
      strings.events.months.july,
      strings.events.months.august,
      strings.events.months.september,
      strings.events.months.october,
      strings.events.months.november,
      strings.events.months.december,
    ];
    return monthNames[date.getMonth()];
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    if (direction === 'prev') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    onDateChange(newDate);
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days: (Date | null)[] = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const getEventsForDate = (date: Date) => {
    // Use local date components to avoid timezone issues
    const calendarYear = date.getFullYear();
    const calendarMonth = date.getMonth();
    const calendarDay = date.getDate();
    
    const filteredEvents = validEvents.filter(event => {
      // Check both date and startDate fields
      const eventDate = event.date || event.startDate;
      if (!eventDate) {
        return false;
      }
      
      // Use the same parseEventDate function
      const parsedDate = parseEventDate(eventDate);
      if (!parsedDate) {
        return false;
      }
      
      // Compare using local date components to avoid timezone issues
      const eventYear = parsedDate.getFullYear();
      const eventMonth = parsedDate.getMonth();
      const eventDay = parsedDate.getDate();
      
      // Match if year, month, and day all match
      const matches = eventYear === calendarYear && 
                     eventMonth === calendarMonth && 
                     eventDay === calendarDay;
      
      return matches;
    });
    
    return filteredEvents;
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const PreviousArrow = () => (
    <ChevronLeft className={`w-5 h-5 text-slate-600 dark:text-slate-400`} />
  );

  const NextArrow = () => (
    <ChevronRight className={`w-5 h-5 text-slate-600 dark:text-slate-400`} />
  );

  const days = getDaysInMonth(currentDate);

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
      {/* Calendar Header */}
      <div className="p-6 border-b border-slate-200 dark:border-slate-700">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-slate-800 dark:text-white">
            {getLocalizedMonthName(currentDate)} {currentDate.getFullYear()}
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigateMonth('prev')}
              className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >
              {language === 'ar' ? <NextArrow /> : <PreviousArrow />}
            </button>
            <button
              onClick={() => navigateMonth('next')}
              className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >
              {language === 'ar' ? <PreviousArrow /> : <NextArrow />}
            </button>
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="p-6">
        {/* Day Headers */}
        <div className="grid grid-cols-7 gap-1 mb-4">
          {Array.from({ length: 7 }, (_, i) => (
            <div key={i} className="text-center text-sm font-medium text-slate-500 dark:text-slate-400 py-2">
              {getLocalizedDayName(i)}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7 gap-1">
          {days.map((day, index) => {
            if (!day) {
              return <div key={index} className="h-24"></div>;
            }

            const dayEvents = getEventsForDate(day);
            const isCurrentDay = isToday(day);

            return (
              <div
                key={day.toISOString()}
                className={`h-24 p-2 border border-slate-200 dark:border-slate-700 rounded-lg ${
                  isCurrentDay 
                    ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-600' 
                    : 'hover:bg-slate-50 dark:hover:bg-slate-700/50'
                }`}
              >
                <div className={`text-sm font-medium mb-1 ${
                  isCurrentDay 
                    ? 'text-blue-600 dark:text-blue-400' 
                    : 'text-slate-700 dark:text-slate-300'
                }`}>
                  {day.getDate()}
                </div>
                
                <div className="space-y-1">
                  {dayEvents.slice(0, 2).map((event) => (
                    <div
                      key={event.id}
                      onClick={() => onEventClick(event)}
                      className="text-xs p-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded cursor-pointer hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors truncate"
                      title={event.legalFileTitle || 'Untitled Event'}
                    >
                      {event.legalFileTitle || 'Untitled Event'}
                    </div>
                  ))}
                  {dayEvents.length > 2 && (
                    <div className="text-xs text-slate-500 dark:text-slate-400">
                      +{dayEvents.length - 2} {language === 'ar' ? 'أخرى' : 'more'}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CalendarView;
